import textStyle from "component/toolManager/textStyle.js";
import draw from "./draw.js";
import fillTextLines from "./fillTextLines.js";
import fillTextLinesDetailStyle from "./fillTextLinesDetailStyle";
//import fillBox from "./fillRadiusBox.js";
import fillRadiusBox from "./fillRadiusBox.js";

/**
 * Compute the width of the box required to display the given `text` with a given `padding`.
 * @public
 * @function textBoxWidth
 * @memberof Drawing
 *
 * @param {CanvasRenderingContext2D} context - Target context
 * @param {String} text - The text to find the width of.
 * @param {Number} padding - The padding to apply on either end of the text.
 * @returns {Number} computed text box width
 */
export function textBoxWidth(context, text, padding) {
  const font = textStyle.getFont();
  const origFont = context.font;

  if (font && font !== origFont) {
    context.font = font;
  }

  let fontSize = textStyle.getFontSize();
  const ratio = Math.max(
    Math.min(context.canvas.height / 20, fontSize) / fontSize,
    0.3
  );
  fontSize = fontSize * ratio;
  const fontFamily = textStyle.getFontFamily();
  context.font = `${fontSize}px "${fontFamily}"`;

  let width = 0;
  if (Array.isArray(text)) {
    width = context.measureText(text[0]).width;
  } else {
    width = context.measureText(text).width;
  }

  if (font && font !== origFont) {
    context.font = origFont;
  }

  return width + 2 * padding;
}

function getTextLines(textLines) {
  let lines = 0;
  if (Array.isArray(textLines)) {
    textLines.forEach(function (text) {
      if (text[2]) {
        lines++;
      }
    });
  } else {
    lines = textLines.length;
  }

  return lines;
}

export function getTextboxBoundingBox(context, textLines, x, y) {
  if (Object.prototype.toString.call(textLines) !== "[object Array]") {
    textLines = [textLines];
  }

  let padding = 5;
  let fontSize = textStyle.getFontSize();
  const ratio = Math.max(
    Math.min(context.canvas.height / 20, fontSize) / fontSize,
    0.3
  );
  fontSize = fontSize * ratio;
  const fontFamily = textStyle.getFontFamily();
  context.font = `${fontSize}px "${fontFamily}"`;
  padding = padding * ratio;

  // Find the longest text width in the array of text data
  let maxWidth = 0;
  let lineWidth = 0;
  textLines.forEach(function (text) {
    // Get the text width in the current font
    const width = textBoxWidth(context, text, padding);
    lineWidth += width;
    const isNewLine = text[2];
    if (isNewLine) {
      // Find the maximum with for all the text rows;
      maxWidth = Math.max(maxWidth, lineWidth);
      lineWidth = 0;
    }
  });
  const lines = getTextLines(textLines);
  // Calculate the bounding box for this text box
  return {
    x: x,
    y: y,
    width: maxWidth,
    height: padding + lines * (fontSize + padding),
    radius: 10 * ratio,
  };
}

/**
 * Draws a textBox.
 * @public
 * @function drawTextBox
 * @memberof Drawing
 *
 * @param  {CanvasRenderingContext2D} context The canvas context.
 * @param  {string} textLines   The text to display.
 * @param  {number} x           The x position of the textBox.
 * @param  {number} y           The y position of the textBox.
 * @param  {string} color       The color of the textBox.
 * @param  {Object} options     Options for the textBox.
 * @returns {Object} {top, left, width, height} - Bounding box; can be used for pointNearTool
 */
export default function (context, textLines, x, y, color, options) {
  if (Object.prototype.toString.call(textLines) !== "[object Array]") {
    textLines = [textLines];
  }

  let padding = 5;
  let fontSize = textStyle.getFontSize();
  const ratio = Math.max(
    Math.min(context.canvas.height / 20, fontSize) / fontSize,
    0.3
  );
  fontSize = fontSize * ratio;
  const fontFamily = textStyle.getFontFamily();
  context.font = `${fontSize}px "${fontFamily}"`;
  padding = padding * ratio;
  const backgroundColor = textStyle.getBackgroundColor();
  const strokeColor = textStyle.getStrokeColor();

  // Find the longest text width in the array of text data
  let maxWidth = 0;
  let lineWidth = 0;
  textLines.forEach(function (text) {
    // Get the text width in the current font
    const width = textBoxWidth(context, text, padding);
    lineWidth += width;
    const isNewLine = text[2];
    if (isNewLine) {
      // Find the maximum with for all the text rows;
      maxWidth = Math.max(maxWidth, lineWidth);
      lineWidth = 0;
    }
  });
  const lines = getTextLines(textLines);
  // Calculate the bounding box for this text box
  const boundingBox = {
    width: maxWidth,
    height: padding + lines * (fontSize + padding),
    //radius: 10
    radius: 10 * ratio,
  };

  draw(context, (context) => {
    context.strokeStyle = color;

    // Draw the background box with padding
    if (options && options.centering && options.centering.x === true) {
      x -= boundingBox.width / 2;
    }

    if (options && options.centering && options.centering.y === true) {
      y -= boundingBox.height / 2;
    }

    boundingBox.left = x;
    boundingBox.top = y;

    const fillStyle =
      options && options.debug === true ? "#FF0000" : backgroundColor;
    const strokeStyle =
      options && options.debug === true ? "#FF0000" : strokeColor;
    //fillBox(context, boundingBox, fillStyle);
    fillRadiusBox(context, boundingBox, fillStyle, strokeStyle);

    // Draw each of the text lines on top of the background box
    if (options.textDetailStyle) {
      fillTextLinesDetailStyle(context, boundingBox, textLines, color, padding);
    } else {
      fillTextLines(context, boundingBox, textLines, color, padding);
    }
  });

  // Return the bounding box so it can be used for pointNearHandle
  return boundingBox;
}
